import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Paper, Tab, Tabs } from "@mui/material";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { OcrService } from "e.sic-platform.webclient/src/services/ocr/OcrService";
import LabelSettingsView from "e.sic-platform.webclient/src/components/labeling/LabelSettingsView";

const t = (key: string) => translationService.translate(key);

// One height for both tabs, so switching never makes the header jump. Tall enough for the order
// summary of a three-service run; anything longer scrolls inside the panel.
const BODY_HEIGHT = 340;

interface OrderAndLabelsPanelProps {
  /** The Order & process details summary, rendered with `embedded`. */
  order: React.ReactNode;
  ocrService: OcrService;
}

/**
 * The header's right-hand panel: what was run and charged, and the text regions the run was
 * labelled with -- two tabs of one box, same width and height.
 *
 * The labels sit here rather than next to Main Information because they are a setting of the
 * RUN, like the services ordered: they decide what OCR read, and they are chosen before it runs.
 */
const OrderAndLabelsPanel: React.FC<OrderAndLabelsPanelProps> = observer(({ order, ocrService }) => {
  const [tab, setTab] = useState<"order" | "labels">("order");

  return (
    <Paper variant="outlined" sx={{ width: "100%", overflow: "hidden" }} data-html2canvas-ignore>
      <Tabs
        value={tab}
        onChange={(_e, next) => setTab(next)}
        variant="fullWidth"
        sx={{
          minHeight: 0,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": { minHeight: 0, py: 1, textTransform: "none", fontSize: "0.9rem" },
          "& .MuiTab-root.Mui-selected": { fontWeight: 700 },
        }}
      >
        <Tab value="order" label={t("ocrService.order.title")} />
        <Tab value="labels" label={t("ocrService.labelSettings.tab")} />
      </Tabs>
      <Box sx={{ height: BODY_HEIGHT, overflowY: "auto", p: 1.5 }}>
        {tab === "order" ? (
          order
        ) : (
          <LabelSettingsView ocrService={ocrService} videoId={ocrService.fileId} />
        )}
      </Box>
    </Paper>
  );
});

export default OrderAndLabelsPanel;
